import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Alert, Button, Segmented, Skeleton, Tag } from 'antd';
import { ArrowLeftOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';

import { PlanData, getVisualizationData } from '@/api/custom';
import { buildPageLoaderBranding } from '@/common/components/page-loader';
import InvestorFinancialPlan from '@/pages/financial-plan/investor-plan';
import { useRoutedInvestor } from '@/pages/investors/use-routed-investor';
import { getInvestorDisplayName } from '@/util/investor-display';
import { useAppSelector } from '@/store/hooks';
import { selectApexEnabled, selectProfileData } from '@/store/features/userinfo/userinfoSlice';
import { financialPlanTitle, formatPlanSharedDate } from '@/pages/financial-plan/planning';
import './index.scss';

type FetchState = 'idle' | 'loading' | 'ready' | 'failed';
type PlanView = 'shared-plan' | 'lite';

const assumptionLabel = (key: string): string =>
  key
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (character) => character.toUpperCase()) || key;

const assumptionValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'Not provided';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : 'None';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const financialPlanFrameBootstrap = (userAnswers?: Record<string, unknown> | null): string => {
  const serializedAnswers = JSON.stringify(userAnswers ?? null).replace(/</g, '\\u003c');

  return `<script>
(function () {
  var embeddedUserAnswers = ${serializedAnswers};

  function hasOwn(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
  }

  function dispatchControlEvents(control) {
    control.dispatchEvent(new Event('input', { bubbles: true }));
    control.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function setControlValue(control, value) {
    if (value === undefined || value === null) return;

    var tagName = control.tagName.toLowerCase();
    var type = (control.getAttribute('type') || '').toLowerCase();

    if (type === 'checkbox') {
      control.checked = Boolean(value);
    } else if (type === 'radio') {
      control.checked = value === true || String(control.value) === String(value);
    } else if (tagName === 'select') {
      control.value = String(value);
    } else {
      control.value = String(value);
    }

    dispatchControlEvents(control);
  }

  function matchingAnswerKeys(control) {
    return [
      control.id,
      control.name,
      control.getAttribute('data-answer-key'),
      control.getAttribute('data-question-id')
    ].filter(Boolean);
  }

  function applyUserAnswers(answers) {
    if (!answers || typeof answers !== 'object') return;

    Array.prototype.forEach.call(
      document.querySelectorAll('input, select, textarea'),
      function (control) {
        var keys = matchingAnswerKeys(control);
        for (var index = 0; index < keys.length; index += 1) {
          var key = keys[index];
          if (hasOwn(answers, key)) {
            setControlValue(control, answers[key]);
            return;
          }
        }
      }
    );

    var submitButton = document.querySelector("button[type='submit']");
    if (submitButton && typeof submitButton.click === 'function') {
      submitButton.click();
    }
  }

  function initializeFinancialPlan(payload) {
    var answers =
      payload && typeof payload === 'object' && hasOwn(payload, 'userAnswers')
        ? payload.userAnswers
        : embeddedUserAnswers;

    applyUserAnswers(answers);

    if (typeof window.updateChart === 'function') {
      window.updateChart();
    }
  }

  window.__wavvestFinancialPlanInitialize = initializeFinancialPlan;
  window.addEventListener('message', function (event) {
    var data = event.data;
    if (data === 'initialize') {
      initializeFinancialPlan(null);
    } else if (data && typeof data === 'object' && data.type === 'wavvest:financial-plan:init') {
      initializeFinancialPlan(data);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initializeFinancialPlan(null);
    });
  } else {
    window.setTimeout(function () {
      initializeFinancialPlan(null);
    }, 0);
  }
})();
</script>`;
};

const buildFinancialPlanFrameSource = (
  sourceCode: string,
  userAnswers?: Record<string, unknown> | null,
): string => {
  const bootstrap = financialPlanFrameBootstrap(userAnswers);

  if (/<\/body>/i.test(sourceCode)) {
    return sourceCode.replace(/<\/body>/i, `${bootstrap}</body>`);
  }

  if (/<\/html>/i.test(sourceCode)) {
    return sourceCode.replace(/<\/html>/i, `${bootstrap}</html>`);
  }

  return `${sourceCode}${bootstrap}`;
};

const InvestorPlanPage: React.FC = () => {
  const { planId } = useParams<{ planId: string }>();
  const { investorId, investor, loadingState } = useRoutedInvestor();
  const apexEnabled = useAppSelector(selectApexEnabled);
  const profileData = useAppSelector(selectProfileData);
  const deploymentFlags = useAppSelector((state) => state.userinfo.deploymentFlags);
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [fetchState, setFetchState] = useState<FetchState>('idle');
  const [activeView, setActiveView] = useState<PlanView>('shared-plan');
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const cognitoId = investor?.cognito_id?.trim();
  const clientName = investor ? getInvestorDisplayName(investor) : 'Client';
  const backTo = `/home/clients/${investorId ?? ''}`;
  const topBarBranding = useMemo(() => buildPageLoaderBranding(profileData), [profileData]);

  const loadPlans = useCallback(async () => {
    if (!cognitoId) {
      setPlans([]);
      setFetchState('ready');
      return;
    }

    setFetchState('loading');
    try {
      const response = await getVisualizationData(cognitoId);
      setPlans(Array.isArray(response) ? response : []);
      setFetchState('ready');
    } catch (error) {
      console.error('Failed to load financial plan:', error);
      setFetchState('failed');
    }
  }, [cognitoId]);

  useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  const plan = useMemo(() => plans.find((candidate) => candidate.id === planId), [planId, plans]);
  const assumptions = useMemo(() => Object.entries(plan?.userAnswers ?? {}), [plan]);
  const frameSource = useMemo(
    () =>
      plan
        ? buildFinancialPlanFrameSource(
            plan.visualizationHtml.sourceCode,
            deploymentFlags.statefulCanvasEnabled ? plan.userAnswers : null,
          )
        : '',
    [deploymentFlags.statefulCanvasEnabled, plan],
  );

  useEffect(() => {
    if (!plan || !investor) return undefined;

    const previousTitle = document.title;
    document.title = `${financialPlanTitle(plan)} · ${getInvestorDisplayName(investor)}`;
    return () => {
      document.title = previousTitle;
    };
  }, [investor, plan]);

  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    try {
      // The frame is intentionally sandboxed into an opaque origin. Use
      // postMessage only; do not grant same-origin access to stored plan HTML.
      iframe.contentWindow.postMessage('initialize', '*');
      iframe.contentWindow.postMessage(
        {
          type: 'wavvest:financial-plan:init',
          userAnswers:
            deploymentFlags.statefulCanvasEnabled && plan?.userAnswers ? plan.userAnswers : null,
        },
        '*',
      );
    } catch (error) {
      console.error('Financial plan frame initialization failed:', error);
    }
  }, [deploymentFlags.statefulCanvasEnabled, plan?.userAnswers]);

  if (!investor && (loadingState === 'idle' || loadingState === 'pending')) {
    return (
      <div className="financial-plan-page">
        <div className="financial-plan-page-body">
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      </div>
    );
  }

  if (!investor) {
    return <Navigate to="/home/clients" replace />;
  }

  const topBar = (
    <header className="financial-plan-page-topbar">
      <span className="financial-plan-page-mark" aria-label={topBarBranding?.logoAlt ?? 'Wavvest'}>
        {topBarBranding?.logoUri ? (
          <img
            className="financial-plan-page-brand-logo"
            src={topBarBranding.logoUri}
            alt={topBarBranding.logoAlt}
          />
        ) : (
          'Wavvest'
        )}
      </span>
      <span className="financial-plan-page-divider" aria-hidden="true" />
      <Link className="financial-plan-page-back" to={backTo}>
        <ArrowLeftOutlined aria-hidden="true" />
        {clientName}
      </Link>
      <div className="financial-plan-page-actions">
        {apexEnabled ? (
          <Segmented
            size="small"
            value={activeView}
            onChange={(value) => setActiveView(value as PlanView)}
            options={[
              { label: 'Shared plan', value: 'shared-plan' },
              { label: 'Plan Lite', value: 'lite' },
            ]}
          />
        ) : null}
        <Tag className="financial-plan-view-only-tag" bordered={false}>
          View only
        </Tag>
      </div>
    </header>
  );

  if (fetchState === 'idle' || fetchState === 'loading') {
    return (
      <div className="financial-plan-page">
        {topBar}
        <div className="financial-plan-page-body">
          <Skeleton active paragraph={{ rows: 10 }} />
        </div>
      </div>
    );
  }

  if (fetchState === 'failed') {
    return (
      <div className="financial-plan-page">
        {topBar}
        <div className="financial-plan-page-body">
          <Alert
            type="warning"
            showIcon
            message={`Couldn't load ${clientName}'s plan`}
            description="The plan is safe, but the service did not respond. Try again in a moment."
            action={
              <Button type="primary" onClick={() => void loadPlans()}>
                Try again
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="financial-plan-page">
        {topBar}
        <div className="financial-plan-page-body">
          <section className="financial-plan-empty-state" aria-label="Plan not found">
            <h1>That plan is not available</h1>
            <p>It may have been removed, or the link may be out of date.</p>
            <Link to={backTo}>
              <Button type="primary">Back to {clientName}</Button>
            </Link>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="financial-plan-page">
      {topBar}
      <main className="financial-plan-page-body">
        <div className="financial-plan-page-title-row">
          <div>
            <div className="financial-plan-page-title">{financialPlanTitle(plan)}</div>
            <p>
              Shared {formatPlanSharedDate(plan.sharedAt)}
              {plan.sharedBy ? ` by ${plan.sharedBy}` : ''}
            </p>
          </div>
        </div>

        {activeView === 'lite' ? (
          <section className="financial-plan-page-lite" aria-label="Financial Plan Lite">
            <InvestorFinancialPlan key={investor.cognito_id} user={investor} />
          </section>
        ) : (
          <>
            <section className="financial-plan-frame-shell" aria-label="Shared financial plan">
              <iframe
                ref={iframeRef}
                title={`${financialPlanTitle(plan)} for ${clientName}`}
                className="financial-plan-frame"
                sandbox="allow-scripts allow-popups allow-forms"
                srcDoc={frameSource}
                onLoad={handleIframeLoad}
              />
            </section>

            {assumptions.length > 0 ? (
              <section className="financial-plan-assumptions">
                <button
                  type="button"
                  className="financial-plan-assumptions-toggle"
                  aria-expanded={assumptionsOpen}
                  onClick={() => setAssumptionsOpen((open) => !open)}
                >
                  {assumptionsOpen ? <UpOutlined /> : <DownOutlined />}
                  {assumptionsOpen ? 'Hide assumptions' : 'Show assumptions behind this plan'}
                </button>
                {assumptionsOpen ? (
                  <dl className="financial-plan-assumptions-list">
                    {assumptions.map(([key, value]) => (
                      <div key={key}>
                        <dt>{assumptionLabel(key)}</dt>
                        <dd>{assumptionValue(value)}</dd>
                      </div>
                    ))}
                  </dl>
                ) : null}
              </section>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
};

export default InvestorPlanPage;
